import React, { useState } from 'react';
import {
  Badge, Button, Field, Modal, Note, Select, Sym, Textarea,
} from '../../../components/app2';
import StatusBadge from '../../account/components/StatusBadge';
import billingService, {
  describeStatus,
  formatMxn,
  formatDateLong,
} from '../../../utils/billingService';
import AlertService from '../../../utils/alertService';

/**
 * Ficha de una suscripción (rediseño jul-2026).
 *
 * Conserva TODA la operación que ya existía —sincronizar con Stripe, override
 * manual de plan/estado con motivo obligatorio, cancelación inmediata y el
 * historial de facturas— sobre el modal del sistema (cierra con Escape, bloquea
 * el scroll y devuelve el foco).
 *
 * Los identificadores de Stripe pasan del encabezado a su propio bloque al pie
 * del detalle: son datos de soporte, no el título de la ficha.
 */

const OVERRIDE_STATUS_OPTIONS = [
  { value: 'active', label: 'Activa (active)' },
  { value: 'trialing', label: 'En prueba (trialing)' },
  { value: 'past_due', label: 'Pago pendiente (past_due)' },
  { value: 'canceled', label: 'Cancelada (canceled)' },
  { value: 'grace_period', label: 'Periodo de gracia (grace_period)' },
  { value: 'no_subscription', label: 'Sin plan (no_subscription)' },
];

/** Dato suelto del detalle: etiqueta chica arriba, valor abajo. */
const Dato = ({ label, children }) => (
  <div style={{ minWidth: 0 }}>
    <div className="a2-tcell-muted" style={{ marginBottom: 3 }}>{label}</div>
    <div className="a2-tcell-strong">{children}</div>
  </div>
);

const SubscriptionDetailModal = ({ subscription, plans, onClose, onUpdated }) => {
  const [busy, setBusy] = useState(false);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overridePlan, setOverridePlan] = useState(subscription?.plan?.id || '');
  const [overrideStatus, setOverrideStatus] = useState(subscription?.status || '');
  const [overrideReason, setOverrideReason] = useState('');

  if (!subscription) return null;
  const status = describeStatus(subscription.status);
  const facturas = Array.isArray(subscription.invoices) ? subscription.invoices : [];

  const handleRefresh = async () => {
    try {
      setBusy(true);
      const fresh = await billingService.admin.refreshSubscription(subscription.id);
      onUpdated?.(fresh);
      AlertService?.success('Suscripción sincronizada', '', { autoClose: true });
    } catch (err) {
      AlertService?.error('Error', err?.response?.data?.error?.message || err?.message);
    } finally {
      setBusy(false);
    }
  };

  const handleCancelNow = async () => {
    const confirmed = await AlertService?.confirm(
      '¿Cancelar de inmediato?',
      'Esto cancela la suscripción AHORA, no al final del período. Úsalo solo en casos excepcionales.',
      { confirmText: 'Sí, cancelar', cancelText: 'No', confirmColor: '#dc2626' },
    );
    if (!confirmed) return;
    try {
      setBusy(true);
      const fresh = await billingService.admin.cancelNow(subscription.id);
      onUpdated?.(fresh);
      AlertService?.success('Suscripción cancelada', '', { autoClose: true });
    } catch (err) {
      AlertService?.error('Error', err?.response?.data?.error?.message || err?.message);
    } finally {
      setBusy(false);
    }
  };

  const handleApplyOverride = async () => {
    if (!overrideReason.trim()) {
      AlertService?.error('Razón requerida', 'Anota el motivo del override.');
      return;
    }
    try {
      setBusy(true);
      const payload = {
        override_reason: overrideReason.trim(),
      };
      if (overridePlan && Number(overridePlan) !== subscription.plan?.id) {
        payload.planId = Number(overridePlan);
      }
      if (overrideStatus && overrideStatus !== subscription.status) {
        payload.status = overrideStatus;
      }
      const fresh = await billingService.admin.overrideSubscription(subscription.id, payload);
      onUpdated?.(fresh);
      setOverrideOpen(false);
      AlertService?.success('Override aplicado', '', { autoClose: true });
    } catch (err) {
      AlertService?.error('Error', err?.response?.data?.error?.message || err?.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={subscription.company?.name || 'Empresa'}
      subtitle={subscription.company?.rfc || 'Sin RFC'}
      maxWidth={760}
      footer={(
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginRight: 'auto' }}>
            <Button variant="outline" size="sm" icon="sync" onClick={handleRefresh} disabled={busy}>
              Sincronizar con Stripe
            </Button>
            {!overrideOpen ? (
              <Button
                variant="outline"
                size="sm"
                icon="edit_note"
                onClick={() => setOverrideOpen(true)}
                disabled={busy}
              >
                Override manual
              </Button>
            ) : null}
          </div>
          {subscription.stripe_subscription_id ? (
            <Button variant="danger" size="sm" icon="block" onClick={handleCancelNow} disabled={busy}>
              Cancelar ahora
            </Button>
          ) : null}
          <Button size="sm" onClick={onClose}>Cerrar</Button>
        </>
      )}
    >
      {/* Estado + plan de un vistazo */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 18 }}>
        <StatusBadge tone={status.tone} label={status.label} />
        {subscription.is_manual_override ? (
          <Badge tone="warn" icon="edit_note">Override manual</Badge>
        ) : null}
        {subscription.cancel_at_period_end ? (
          <Badge tone="warn" icon="event_busy">Cancela al terminar el periodo</Badge>
        ) : null}
        {subscription.applied_coupon?.code ? (
          <Badge tone="brand" icon="sell">Cupón {subscription.applied_coupon.code}</Badge>
        ) : null}
      </div>

      {/* Datos del contrato */}
      <div
        style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))',
          gap: 14, marginBottom: 18,
        }}
      >
        <Dato label="Plan">{subscription.plan?.name || '—'}</Dato>
        <Dato label="Precio del plan">
          {subscription.plan ? `${formatMxn(subscription.plan.price_mxn)} / mes` : '—'}
        </Dato>
        <Dato label="Empleados (snapshot)">
          {subscription.employee_count_snapshot ?? '—'}
          {subscription.plan?.max_employees != null ? (
            <span className="a2-t-faint"> / {subscription.plan.max_employees}</span>
          ) : null}
        </Dato>
        <Dato label="Periodo actual">
          {formatDateLong(subscription.current_period_start)}
          <span className="a2-t-faint"> a </span>
          {formatDateLong(subscription.current_period_end)}
        </Dato>
        <Dato label="Última sincronización">
          {formatDateLong(subscription.last_synced_at)}
        </Dato>
      </div>

      {subscription.override_reason ? (
        <Note tone="warn" icon="edit_note" style={{ marginBottom: 18 }}>
          <strong>Motivo del override:</strong> {subscription.override_reason}
        </Note>
      ) : null}

      {/* Historial de facturas */}
      {facturas.length > 0 ? (
        <div style={{ marginBottom: 18 }}>
          <h4 className="a2-card-title" style={{ fontSize: '1rem', marginBottom: 10 }}>
            Últimas facturas ({facturas.length})
          </h4>
          <div className="a2-surface-2" style={{ borderRadius: 12, overflow: 'hidden' }}>
            {facturas.slice(0, 6).map((inv, i) => (
              <div
                key={inv.id}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  gap: 12, padding: '10px 14px',
                  borderTop: i === 0 ? 'none' : '1px solid var(--line)',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div className="a2-tcell-strong a2-truncate">{inv.number || `#${inv.id}`}</div>
                  <div className="a2-tcell-muted">
                    {formatDateLong(inv.paid_at || inv.period_start)}
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div className="a2-tcell-strong">{formatMxn(inv.amount_paid)}</div>
                  <div className="a2-tcell-muted">{inv.status}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {/* Override manual */}
      {overrideOpen ? (
        <div
          className="a2-surface-2 a2-anim-fadein"
          style={{ borderRadius: 12, border: '1px solid var(--line-2)', padding: 16, marginBottom: 18 }}
        >
          <h4 className="a2-card-title" style={{ fontSize: '1rem', marginBottom: 4 }}>
            Aplicar override manual
          </h4>
          <p className="a2-card-sub" style={{ marginBottom: 14 }}>
            Cambia plan o estado sin pasar por Stripe. Queda registrado con su motivo.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 12 }}>
            <Field label="Plan" htmlFor="override-plan">
              <Select
                id="override-plan"
                value={overridePlan}
                onChange={(e) => setOverridePlan(e.target.value)}
              >
                <option value="">— No cambiar —</option>
                {plans.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} ({formatMxn(p.price_mxn)})
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Estado" htmlFor="override-status">
              <Select
                id="override-status"
                value={overrideStatus}
                onChange={(e) => setOverrideStatus(e.target.value)}
              >
                <option value="">— No cambiar —</option>
                {OVERRIDE_STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </Select>
            </Field>
          </div>

          <Field
            label="Motivo (requerido)"
            htmlFor="override-reason"
            hint="Queda guardado en la suscripción para auditoría."
            style={{ marginTop: 12 }}
          >
            <Textarea
              id="override-reason"
              rows={2}
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
              placeholder="Ej: ajuste por demo otorgada al cliente X"
            />
          </Field>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
            <Button variant="outline" size="sm" onClick={() => setOverrideOpen(false)} disabled={busy}>
              Cancelar
            </Button>
            <Button size="sm" icon="check" onClick={handleApplyOverride} loading={busy}>
              Aplicar override
            </Button>
          </div>
        </div>
      ) : null}

      {/* Identificadores de Stripe (soporte) */}
      <div
        style={{
          display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center',
          paddingTop: 14, borderTop: '1px solid var(--line)',
        }}
      >
        <Sym name="link" size={16} style={{ color: 'var(--faint)' }} />
        <span className="a2-tcell-muted">Stripe · cliente</span>
        <span className="a2-mono a2-tcell-muted">
          {subscription.stripe_customer_id || 'sin Stripe'}
        </span>
        <span className="a2-t-faint">·</span>
        <span className="a2-tcell-muted">suscripción</span>
        <span className="a2-mono a2-tcell-muted">
          {subscription.stripe_subscription_id || 'sin Stripe'}
        </span>
      </div>
    </Modal>
  );
};

export default SubscriptionDetailModal;
